import { getSessionSaga } from 'metrix-common3';
import { call, delay } from 'redux-saga/effects';

/** Long enough not to hammer the endpoint, short enough that a quick bind is not made to wait. */
const POLL_INTERVAL_MS = 2000;

/**
 * Counted, not clocked. A wall-clock deadline is defeated by a throttled background tab or a
 * sleeping laptop, where a single clamped timer can swallow the whole budget in one tick.
 */
const POLL_ATTEMPTS = 10;

/**
 * Wait until a policy transaction has actually moved on, or give up.
 *
 * Several actions — charging a card, accepting a quote, requesting or confirming a bind — answer as
 * soon as the work is handed off, while binding the policy and issuing its documents carry on
 * server-side. Reloading on that answer shows the page exactly as it was, and the only way to see
 * the result is to refresh again by hand.
 *
 * The status in the summary is what the page is judged by: it drives the badge and the action bar,
 * so a change there is the moment a reload becomes worth doing. The payment endpoint would answer a
 * narrower question — money moved — and money can move a beat before the record catches up, which
 * is the same too-early reload in a new place.
 *
 * Deliberately a bare fetch rather than `getPolicySummarySaga`: that saga writes to the store on
 * every call, so polling through it would replace the operation message the caller just produced,
 * and would re-raise its own error on each attempt if the endpoint hiccuped — an error modal
 * reopening every couple of seconds behind a spinner, for work that was succeeding all along.
 *
 * @returns `true` if the status changed, `false` if it never did within the budget — the caller
 * decides what that means, since giving up is not the same as failing.
 */
export function* waitForPolicyStatusChangeSaga(entity, statusBefore) {
    // Nothing to compare against. A first-seen status is indistinguishable from a changed one, and
    // guessing wrong here means declaring the work finished the moment it started.
    if (statusBefore === undefined || statusBefore === null) {
        return false;
    }

    const session = yield getSessionSaga();

    for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt++) {
        try {
            const response = yield call(fetch, `/api/policyTransaction/summary/${entity}`, {
                method: 'GET',
                credentials: 'same-origin',
                headers: {
                    Accept: 'application/json',
                    Authorization: session.authenticationToken.token,
                    'Content-Type': 'application/json',
                },
            });
            const result = yield call([response, 'json']);
            if (result?.policySummary?.status && result.policySummary.status !== statusBefore) {
                return true;
            }
        } catch (error) {
            // One failed look is not a reason to stop waiting for work that is still running.
            console.log('Error while polling policy summary - ', error);
        }

        // No wait after the last look — the answer will not change before we give up on it.
        if (attempt < POLL_ATTEMPTS - 1) {
            yield delay(POLL_INTERVAL_MS);
        }
    }

    return false;
}
